import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter, type Href } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { practiceApi } from "@/api";
import { socialStrings } from "@/i18n/features/social.i18n";
import { notify } from "@/lib/dialog";
import { practiceScenarioCopy } from "@/lib/practice-labels";
import i18n from "@/i18n";
import { useAppTheme } from "@/theme/app-theme";
import type {
  PracticeScenario,
  PracticeSession,
} from "@/types";

/**
 * Conversation Practice — scenario chooser. This is a private roleplay sandbox;
 * it is intentionally not part of the real chats inbox.
 */
export default function PracticeHomeScreen() {
  useTranslation();
  const { colors } = useAppTheme();
  const router = useRouter();

  const [scenarios, setScenarios] = useState<PracticeScenario[]>([]);
  const [activeSessions, setActiveSessions] = useState<PracticeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  const activeByScenario = useMemo(
    () =>
      new Map(activeSessions.map((session) => [session.scenarioId, session])),
    [activeSessions],
  );

  const goBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace("/inbox");
  }, [router]);

  const load = useCallback(async () => {
    try {
      const [list, sessions] = await Promise.all([
        practiceApi.scenarios(),
        practiceApi.activeSessions(),
      ]);
      setScenarios(list);
      setActiveSessions(sessions);
    } catch {
      notify(i18n.t("practice.errors.load"), i18n.t("practice.tryAgain"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh on focus so returning from a session reflects which scenarios are
  // still active.
  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const start = useCallback(
    async (scenario: PracticeScenario) => {
      if (starting) return;
      const activeSession = activeByScenario.get(scenario.id);
      if (activeSession) {
        router.push(`/practice/${activeSession.id}`);
        return;
      }

      setStarting(scenario.id);
      try {
        const { session } = await practiceApi.startSession(scenario.id);
        setActiveSessions((prev) => {
          const rest = prev.filter((s) => s.scenarioId !== session.scenarioId);
          return [session, ...rest];
        });
        router.push(`/practice/${session.id}`);
      } catch {
        notify(
          i18n.t("practice.practiceAlert"),
          i18n.t("practice.errors.start"),
        );
      } finally {
        setStarting(null);
      }
    },
    [activeByScenario, router, starting],
  );

  return (
    <SafeAreaView
      style={[styles.screen, { backgroundColor: colors.background }]}
      edges={["top"]}
    >
      <StatusBar style={colors.background === "#0D1724" ? "light" : "dark"} />

      <View style={styles.header}>
        <Pressable onPress={goBack} hitSlop={12} style={styles.backBtn}>
          <Ionicons name="chevron-back" size={26} color={colors.textStrong} />
        </Pressable>
        <Text
          maxFontSizeMultiplier={1.5}
          numberOfLines={2}
          style={[styles.headerTitle, { color: colors.textStrong }]}
        >
          {i18n.t("practice.title")}
        </Text>
        <View style={styles.backBtn} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.body}
        >
          <Text
            maxFontSizeMultiplier={1.5}
            style={[styles.chooseLabel, { color: colors.textStrong }]}
          >
            {i18n.t("practice.chooseScenario")}
          </Text>

          {/* Open by default: this screen has one job, choosing a scenario. */}
          <Pressable
            onPress={() => setOpen((o) => !o)}
            style={({ pressed }) => [
              styles.dropdownTrigger,
              {
                backgroundColor: colors.surface,
                borderColor: open ? colors.primary : colors.border,
                opacity: pressed ? 0.9 : 1,
              },
            ]}
          >
            <Text
              maxFontSizeMultiplier={1.35}
              style={[styles.dropdownTriggerText, { color: colors.textStrong }]}
            >
              {i18n.t("practice.chooseHint")}
            </Text>
            <Ionicons
              name={open ? "chevron-up" : "chevron-down"}
              size={20}
              color={colors.textMuted}
              style={styles.dropdownChevron}
            />
          </Pressable>

          {open && (
            <View style={styles.dropdownList}>
              {scenarios.map((s) => {
                const copy = practiceScenarioCopy(s);
                const activeSession = activeByScenario.get(s.id);
                const isActive = !!activeSession;
                return (
                  <Pressable
                    key={s.id}
                    onPress={() => start(s)}
                    disabled={!!starting && starting !== s.id}
                    style={({ pressed }) => [
                      styles.scenario,
                      {
                        backgroundColor: colors.surface,
                        borderColor: isActive ? colors.primary : colors.border,
                        opacity: pressed ? 0.85 : 1,
                      },
                    ]}
                  >
                    <View style={styles.scenarioCopy}>
                      <View style={styles.scenarioTitleRow}>
                        <Text
                          maxFontSizeMultiplier={1.4}
                          style={[
                            styles.scenarioTitle,
                            { color: colors.textStrong },
                          ]}
                        >
                          {copy.title}
                        </Text>
                        {isActive && (
                          <Text
                            maxFontSizeMultiplier={1.2}
                            numberOfLines={2}
                            style={[
                              styles.activePill,
                              {
                                backgroundColor: colors.primarySoft,
                                color: colors.primaryStrong,
                              },
                            ]}
                          >
                            {i18n.t("practice.active")}
                          </Text>
                        )}
                      </View>
                      <Text
                        maxFontSizeMultiplier={1.45}
                        style={[
                          styles.scenarioDesc,
                          { color: colors.textSecondary },
                        ]}
                      >
                        {copy.description}
                      </Text>
                    </View>
                    <View style={styles.scenarioTrailing}>
                      {starting === s.id ? (
                        <ActivityIndicator color={colors.primary} />
                      ) : (
                        <Ionicons
                          name={
                            isActive
                              ? "chatbubble-ellipses-outline"
                              : "chevron-forward"
                          }
                          size={20}
                          color={isActive ? colors.primary : colors.textMuted}
                        />
                      )}
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}
        </ScrollView>
      )}

      <PracticeBottomNav />
    </SafeAreaView>
  );
}

function PracticeBottomNav() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const bottomInset = Platform.OS === "android" ? insets.bottom : 0;

  const tabs: Array<{
    label: string;
    route: Href;
    icon: keyof typeof Ionicons.glyphMap;
    activeIcon: keyof typeof Ionicons.glyphMap;
    active?: boolean;
  }> = [
    {
      label: t(socialStrings.navHome),
      route: "/home",
      icon: "location-outline",
      activeIcon: "location",
    },
    {
      label: t(socialStrings.navCues),
      route: "/cues",
      icon: "chatbubbles-outline",
      activeIcon: "chatbubbles",
    },
    {
      label: t(socialStrings.navInbox),
      route: "/inbox",
      icon: "mail-outline",
      activeIcon: "mail",
      active: true,
    },
    {
      label: t(socialStrings.navProfile),
      route: "/profile",
      icon: "person-circle-outline",
      activeIcon: "person-circle",
    },
  ];

  return (
    <View
      style={[
        styles.bottomNav,
        {
          height: (Platform.OS === "ios" ? 88 : 64) + bottomInset,
          paddingBottom: bottomInset,
        },
      ]}
    >
      {tabs.map((tab) => {
        const color = tab.active ? TAB_BAR_ACTIVE : TAB_BAR_INACTIVE;

        return (
          <Pressable
            key={tab.label}
            accessibilityRole="tab"
            accessibilityState={{ selected: tab.active }}
            onPress={() => router.replace(tab.route)}
            style={styles.bottomNavItem}
          >
            <Ionicons
              name={tab.active ? tab.activeIcon : tab.icon}
              size={26}
              color={color}
              style={styles.bottomNavIcon}
            />
            <Text
              numberOfLines={1}
              maxFontSizeMultiplier={1.2}
              style={[styles.bottomNavLabel, { color }]}
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const TAB_BAR_BACKGROUND = "#19283B";
const TAB_BAR_ACTIVE = "#78AEFF";
const TAB_BAR_INACTIVE = "rgba(255, 255, 255, 0.68)";
const TAB_BAR_SHADOW = "#102D51";

const styles = StyleSheet.create({
  screen: { flex: 1 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  backBtn: {
    width: 34,
    height: 34,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 17,
    fontWeight: "700",
    lineHeight: 22,
    textAlign: "center",
  },
  center: { flex: 1, alignItems: "center", justifyContent: "center" },
  content: { flex: 1 },
  body: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    padding: 16,
    gap: 12,
  },
  chooseLabel: {
    minWidth: 0,
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 21,
    marginTop: 4,
  },
  dropdownTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    minHeight: 64,
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
  },
  dropdownTriggerText: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 15,
    fontWeight: "600",
    lineHeight: 20,
  },
  dropdownChevron: {
    width: 24,
    flexShrink: 0,
    textAlign: "right",
  },
  dropdownList: { gap: 10 },
  scenario: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    minWidth: 0,
    minHeight: 64,
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
  },
  scenarioCopy: { flex: 1, minWidth: 0 },
  scenarioTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  scenarioTitle: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
  },
  scenarioDesc: { fontSize: 13, marginTop: 3, lineHeight: 18 },
  activePill: {
    overflow: "hidden",
    maxWidth: "100%",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  scenarioTrailing: {
    width: 24,
    minHeight: 24,
    flexShrink: 0,
    alignItems: "flex-end",
    alignSelf: "center",
    justifyContent: "center",
  },
  bottomNav: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    paddingTop: 6,
    borderTopWidth: 0,
    backgroundColor: TAB_BAR_BACKGROUND,
    shadowColor: TAB_BAR_SHADOW,
    shadowOpacity: 0.22,
    shadowRadius: 12,
    shadowOffset: {
      width: 0,
      height: -4,
    },
    elevation: 10,
  },
  bottomNavItem: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    flexDirection: Platform.OS === "web" ? "row" : "column",
    alignItems: "center",
    justifyContent: "center",
    gap: Platform.OS === "web" ? 8 : 2,
  },
  bottomNavIcon: {
    marginTop: 1,
  },
  bottomNavLabel: {
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "600",
  },
});
